/** The piece that came out of a pen wall, lying where it fell. */
import * as THREE from 'three';
import { surface } from '../materials';
import type { AssetDefinition } from '../types';
import { getZone } from '@/render/palette';

/**
 * The slab, and it is exported because the room needs it twice.
 *
 * `fattening.ts` places this asset and builds its collider from these numbers,
 * so what she walks around and what she sees are the same slab. That is the
 * pattern `BLACKOUT_OTTO_GATE_AT` already uses one file over — one transform
 * driving picture and collision — and it is the room's answer to D-056 and
 * D-059, both of which were two lists built from the same constants drifting
 * apart.
 */
export const PEN_RUBBLE_SLAB = { x: 0.30, y: 0.06, z: 0.40 } as const;

/**
 * How far it is tipped off flat, radians about Z.
 *
 * Small. It is a slab lying on concrete, not a headstone — but a box sitting
 * perfectly flat and perfectly square reads as level geometry rather than as
 * something that fell, and this asset has exactly one job.
 */
const TIP = 0.05;

function zoneSurfaces(): readonly [number, number, number, number, number] {
  const surfaces = getZone('fattening')?.surfaces;
  if (!surfaces || surfaces.length < 5) throw new Error('fattening zone needs five surface colours');
  return [surfaces[0]!, surfaces[1]!, surfaces[2]!, surfaces[3]!, surfaces[4]!];
}

function box(size: readonly [number, number, number], colour: number, name: string): THREE.Mesh {
  const mesh = new THREE.Mesh(new THREE.BoxGeometry(...size), surface('mineral', colour));
  mesh.name = name;
  mesh.castShadow = true;
  mesh.receiveShadow = true;
  return mesh;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'pen-rubble';
  const [, concrete] = zoneSurfaces();

  const slab = box([PEN_RUBBLE_SLAB.x, PEN_RUBBLE_SLAB.y, PEN_RUBBLE_SLAB.z], concrete, 'fallen-slab');
  slab.rotation.z = TIP;
  // Tipping about Z lifts one long edge and drops the other, so the resting
  // height is the half-thickness plus what the tip pushes down. Computed rather
  // than measured by eye: `asset.spec` fails an asset that floats or sinks by
  // two millimetres, and TIP has already been changed once.
  slab.position.y = PEN_RUBBLE_SLAB.y / 2 * Math.cos(TIP) + PEN_RUBBLE_SLAB.x / 2 * Math.sin(TIP);
  group.add(slab);

  // What broke off the piece that broke off. Kept **symmetric about the origin**
  // — the fragments are what let the asset's measured bounds stay centred on it,
  // which is the origin contract rather than a decoration.
  for (const [size, x, z, rotY, name] of [
    [[0.09, 0.035, 0.12], -0.19, 0.11, -0.16, 'fragment-near'],
    [[0.07, 0.025, 0.09], 0.18, -0.14, 0.18, 'fragment-far'],
    [[0.06, 0.02, 0.075], 0.14, 0.20, -0.08, 'fragment-side'],
    [[0.05, 0.02, 0.06], -0.15, -0.21, 0.42, 'fragment-shed'],
  ] as const) {
    const fragment = box(size, concrete, name);
    fragment.position.set(x, size[1] / 2, z);
    fragment.rotation.y = rotY;
    group.add(fragment);
  }

  return group;
}

/**
 * Broken precast, as its own placement rather than as part of the wall it fell off.
 *
 * **It was inside `pen-wall-broken` first, and the contract is why it is not.**
 * A 2.4 m wall module with a slab lying beside it does not measure centred on
 * its own origin, and `asset.spec` enforces that at 20 mm. Balancing the bounds
 * by adding a second lump on the far side of the panel would have been the gate
 * satisfied rather than the room built, and `CLAUDE.md` has a decision about
 * exactly that shape of fix.
 *
 * Splitting it costs one draw call and buys a wall module that is honest about
 * what it is, plus rubble that can be dropped anywhere else a wall has failed.
 */
export const penRubbleAsset: AssetDefinition = {
  id: 'pen-rubble',
  label: 'Broken pen wall rubble',
  description: 'A fallen slab of precast pen wall and the fragments off it.',
  footprint: { width: 0.42, height: 0.07, length: 0.5 },
  create: build,
};
